<template>
    <div>
        <b-row class="mt-3">
            <b-col :cols="4" class="text-center">
                <b-button
                    class="btn-circle"
                    :class="disabledButtonClass"
                    variant="info"
                    @click="handleDownloadExcel"
                    :disabled="disabledButton"
                >
                    <i v-if="excelLoading" class="fas fa-spinner fa-spin fa-fw fa-lg"></i>
                    <i v-else class="fas fa-file-excel fa-fw fa-lg"></i>
                </b-button>
                <p :class="cardTextClass">
                    <small>
                        {{ $t('org.billing.XLSX of Usage Summary') }}
                    </small>
                </p>
            </b-col>
            <b-col :cols="4" class="text-center">
                <b-button
                    class="btn-circle"
                    :class="disabledButtonClass"
                    variant="success"
                    @click="handleDownloadCSV"
                    :disabled="disabledButton"
                >
                    <i v-if="csvLoading" class="fas fa-spinner fa-spin fa-fw fa-lg"></i>
                    <i v-else class="fas fa-file-csv fa-fw fa-lg"></i>
                </b-button>
                <p :class="cardTextClass">
                    <small>
                        {{ $t('org.billing.CSV of Usage Details') }}
                    </small>
                </p>
            </b-col>
            <b-col :cols="4" class="text-center">
                <b-button
                    class="btn-circle"
                    :class="disabledPDFButtonClass"
                    style="cursor:not-allowed"
                    variant="danger"
                    @click="handleDownloadPDF"
                    :disabled="disabledPDFButton"
                >
                    <i v-if="pdfLoading" class="fas fa-spinner fa-spin fa-fw fa-lg"></i>
                    <i v-else class="fas fa-file-pdf fa-fw fa-lg"></i>
                </b-button>
                <p :class="editMode ? false : hasInvoice ? cardTextClass : 'text-disabled'">
                    <small>
                        {{ $t('org.billing.PDF of Usage Statement') }}
                    </small>
                </p>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import { computed, getCurrentInstance, watch } from '@vue/composition-api'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import useSelectProjectAndGroup from '@/composables/useSelectProjectAndGroup'
import useSelectMonth from '@/composables/useSelectMonth'
import useSummaryDownload from '@/composables/useSummaryDownload'
export default {
    props: ['editMode'],
    setup (props, context) {
        const vm = getCurrentInstance().proxy
        const store = vm.$store
        const currentParams = computed(() => store.getters['$_dashboard/currentParams'])
        const { selectedMonth } = useSelectMonth()
        const { selectedProject, qtypeParam } = useSelectProjectAndGroup()
        const {
            downloadInvoicePDF,
            downloadDetailCSV,
            downloadSimpleExcel,
            getAvailableInvoice
        } = useSummaryDownload()
        const locale = computed(() => vm.$i18n.locale)
        const [pdfStatus, pdfLoading] = downloadInvoicePDF()
        const [csvStatus, csvLoading] = downloadDetailCSV()
        const [excelStatus, excelLoading] = downloadSimpleExcel()
        const [hasInvoiceStatus, hasInvoice] = getAvailableInvoice()

        /** computed */
        if (qtypeParam.value.qtype === 'project') {
            getAvailableInvoice({
                usageAccountId: selectedProject.value.usageAccountId,
                month: selectedMonth.value
            })
        }
        const disabledButton = computed(() => !selectedProject.value || status.value === 204 ? 'disabled' : null)
        const disabledPDFButton = computed(() => disabledButton.value === 'disabled' || !hasInvoice.value ? 'disabled' : null)
        const disabledButtonClass = computed(() => ({
            'cursor-not-allowed': disabledButton.value === 'disabled'
        }))
        const disabledPDFButtonClass = computed(() => ({
            'cursor-not-allowed': !hasInvoice.value || disabledButton.value === 'disabled'
        }))
        const cardTextClass = computed(() => ({
            'text-disabled': disabledButton.value
        }))

        const handleDownloadPDF = () => {
            if (!hasInvoice.value) return
            downloadInvoicePDF({
                usageAccountId: selectedProject.value.usageAccountId,
                month: selectedMonth.value,
                locale: locale.value
            })[2].then((data) => {
                const fileURL = window.URL.createObjectURL(new Blob([data]))
                const fileLink = document.createElement('a')
                fileLink.href = fileURL
                fileLink.setAttribute('download', 'invoice.pdf')
                document.body.appendChild(fileLink)
                fileLink.click()
            })
        }
        const handleDownloadCSV = () => {
            if (disabledButton.value) return
            downloadDetailCSV({
                usageAccountId: selectedProject.value.usageAccountId,
                month: selectedMonth.value
            })[2].then(() => {
                vm.$toast({
                    component: ToastificationContent,
                    position: 'top-right',
                    props: {
                        title: vm.$t('org.message.Creating CSV file'),
                        icon: 'CoffeeIcon',
                        variant: 'success'
                    }
                })
            })
        }
        const handleDownloadExcel = () => {
            if (disabledButton.value) return
            downloadSimpleExcel({
                usageAccountId: selectedProject.value.usageAccountId,
                month: selectedMonth.value
            })[2].then(data => {
                const fileURL = window.URL.createObjectURL(new Blob([data]))
                const fileLink = document.createElement('a')
                fileLink.href = fileURL
                fileLink.setAttribute('download', `${selectedProject.value.usageAccountId}-AWS-Simple.xlsx`)
                document.body.appendChild(fileLink)
                fileLink.click()
            })
        }
        watch(currentParams, (value, oldValue) => {
            if (qtypeParam.value.qtype === 'project') {
                getAvailableInvoice({
                    usageAccountId: selectedProject.value.usageAccountId,
                    month: selectedMonth.value
                })
            }
        }, {
            deep: true
        })
        return {
            pdfStatus,
            csvStatus,
            excelStatus,
            hasInvoiceStatus,
            pdfLoading,
            csvLoading,
            excelLoading,
            hasInvoice,
            disabledButton,
            disabledPDFButton,
            disabledButtonClass,
            disabledPDFButtonClass,
            cardTextClass,
            handleDownloadPDF,
            handleDownloadCSV,
            handleDownloadExcel
        }
    }
}
</script>

<style scoped>
.btn-circle {
    border-radius: 50% !important;
    padding: 12px 9px;
}
.btn[disabled] {
    opacity: 0.25 !important;
}
.text-disabled {
    color: #B4B4B4 !important;
}
</style>
